import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, X, Loader2 } from "lucide-react";
import CookieImage from "../components/CookieImage";
import { api, errorMessage } from "../lib/api";

const IMAGE_KEYS = ["brookies", "cake_batter", "snickerdoodle", "brown_butter"];

export default function AdminFlavors() {
  const [flavors, setFlavors] = useState([]);
  const [uploadingId, setUploadingId] = useState(null);
  const fileRefs = useRef({});

  const load = () => api.get("/flavors").then(r => setFlavors(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const update = (id, field, value) => {
    setFlavors(fs => fs.map(f => f.id === id ? { ...f, [field]: value } : f));
  };

  const save = async (f) => {
    try {
      await api.put(`/admin/flavors/${f.id}`, { name: f.name, description: f.description, image_key: f.image_key, active: f.active });
      toast.success("Saved");
    } catch (e) { toast.error(errorMessage(e)); }
  };

  const uploadImage = async (f, file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error("Image must be 2MB or smaller"); return; }
    setUploadingId(f.id);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post(`/admin/flavors/${f.id}/image`, form, { headers: { "Content-Type": "multipart/form-data" } });
      update(f.id, "image_url", data.image_url);
      toast.success("Image uploaded");
    } catch (e) { toast.error(errorMessage(e)); }
    finally { setUploadingId(null); }
  };

  const removeImage = async (f) => {
    if (!window.confirm("Remove this custom image and revert to the default?")) return;
    try {
      await api.delete(`/admin/flavors/${f.id}/image`);
      update(f.id, "image_url", "");
      toast.success("Image removed");
    } catch (e) { toast.error(errorMessage(e)); }
  };

  return (
    <div data-testid="admin-flavors">
      <div className="mb-6">
        <h1 className="font-display font-extrabold text-3xl md:text-4xl">Weekly Flavors</h1>
        <p className="text-gray-500 mt-1 text-sm">Edit name, description, and upload a fresh photo each week (max 2MB).</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {flavors.map(f => (
          <div key={f.id} className="bg-white border border-gray-100 rounded-2xl p-5 flex gap-4" data-testid={`admin-flavor-card-${f.image_key}`}>
            <div className="flex flex-col items-center gap-2 flex-shrink-0">
              <CookieImage flavorKey={f.image_key} imageUrl={f.image_url} className="w-24 h-24 rounded-full shadow" />
              <input
                ref={el => (fileRefs.current[f.id] = el)}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                className="hidden"
                onChange={e => uploadImage(f, e.target.files?.[0])}
                data-testid={`admin-flavor-file-${f.image_key}`}
              />
              <button
                type="button"
                onClick={() => fileRefs.current[f.id]?.click()}
                disabled={uploadingId === f.id}
                className="text-[11px] font-bold uppercase tracking-wider text-[#FF1F87] flex items-center gap-1 hover:underline disabled:opacity-60"
                data-testid={`admin-flavor-upload-${f.image_key}`}
              >
                {uploadingId === f.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                {f.image_url ? "Replace" : "Upload"}
              </button>
              {f.image_url ? (
                <button
                  type="button"
                  onClick={() => removeImage(f)}
                  className="text-[10px] text-gray-400 flex items-center gap-1 hover:text-red-500"
                  data-testid={`admin-flavor-remove-image-${f.image_key}`}
                >
                  <X className="w-3 h-3" /> Remove
                </button>
              ) : null}
            </div>
            <div className="flex-1 space-y-2">
              <input className="input-base !py-2" value={f.name} onChange={e => update(f.id, "name", e.target.value)} data-testid={`admin-flavor-name-${f.image_key}`} />
              <textarea className="input-base !py-2 min-h-[80px]" value={f.description} onChange={e => update(f.id, "description", e.target.value)} data-testid={`admin-flavor-desc-${f.image_key}`} />
              <div className="flex items-center gap-2 flex-wrap">
                <select className="input-base !py-2 !w-auto" value={f.image_key} onChange={e => update(f.id, "image_key", e.target.value)} disabled={!!f.image_url} title={f.image_url ? "Custom image overrides this default" : ""}>
                  {IMAGE_KEYS.map(k => <option key={k} value={k}>{k}</option>)}
                </select>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={f.active} onChange={e => update(f.id, "active", e.target.checked)} /> Active
                </label>
                <button onClick={() => save(f)} className="ml-auto btn-pink !py-2 !px-4 text-xs" data-testid={`admin-flavor-save-${f.image_key}`}>SAVE</button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
